<script setup lang="ts">
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { UserDataPayload, UserDataResponse } from "@/types/interface/user";
import { useUserStore } from "@/stores/master/user";
import { useRegionStore } from "@/stores/master/region";
import { mapOptionMultiselect } from "@/utils/helper";

const idUser = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const userStore = useUserStore();
const detailUser = ref<UserDataResponse>();
const titlePage = ref("Form Create User");
const listPhoneCode = ref<any>([]);
const regionStore = useRegionStore();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    phone_code: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.string()
    ),
    email: zod.string().email().nonempty({ message: "Email is required" }),
    work_phone: zod.string().nonempty(),
    username: zod.string().nonempty({ message: "Username is required" }),
    password: zod.string().min(6).nonempty({ message: "Password is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: phone_code } = useField<string>("phone_code");
const { value: email } = useField<string>("email");
const { value: work_phone } = useField<string>("work_phone");
const { value: username } = useField<string>("username");
const { value: password } = useField<string>("password");

const onSubmit = handleSubmit(async () => {
  isLoading.value = true;
  const payloadUser: UserDataPayload = {
    name: name.value,
    phone_code: phone_code.value?.value,
    email: email.value,
    phone: work_phone.value,
    username: username.value,
    password: password.value,
    role_id: 1,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await userStore.dispatchUpdate(
      idUser.value,
      payloadUser
    );
  } else {
    responseApi.value = await userStore.dispatchCreate(payloadUser);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value ? "User Successfully Updated" : "User Successfully Added"
      );
      router.push("/master/user");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailUser = async () => {
  const { success, message } = await userStore.dispatchDetail(idUser.value);
  if (success) {
    detailUser.value = await userStore?.user?.data;
    name.value = detailUser.value?.name;
    phone_code.value = { value: "62", label: "+62 - Indonesia" };
    email.value = detailUser.value?.email;
    work_phone.value = detailUser.value?.phone;
    username.value = detailUser.value?.username;
  }
};

const loadCountry = async () => {
  const { success, message } = await regionStore.dispatchGetCountry();
  listPhoneCode.value = mapOptionMultiselect(
    await regionStore.countries,
    "Phonecode",
    "phone_name"
  );
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idUser.value = router.currentRoute.value.params.id.toString();
    titlePage.value = "Form Edit User";
    loadDetailUser();
  }
  loadCountry();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">{{ titlePage }}</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/user"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List User
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-user-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Email <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="email"
                      v-model="email"
                      placeholder="Email"
                      type="email"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-mail-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.email }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Phone Code <span class="text-danger">*</span></label
                  >
                  <vSelect
                    :options="listPhoneCode"
                    name="phone_code"
                    v-model="phone_code"
                    placeholder="Phone Code"
                  ></vSelect>
                  <span class="text-danger">{{ errors.phone_code }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Work Phone <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="work_phone"
                      v-model="work_phone"
                      placeholder="81217018168"
                      type="text"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-phone-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.work_phone }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Username <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="username"
                      v-model="username"
                      placeholder="Username"
                      type="text"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-user-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.username }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Password <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="password"
                      v-model="password"
                      placeholder="Password"
                      type="text"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-lock-fill position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.password }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
